import React, { createContext, useContext, useState, ReactNode } from 'react';

export type UserRole = 'rep' | 'manager' | 'executive';

export interface MockUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: UserRole;
  team: string;
  title: string;
}

const mockUsers: Record<UserRole, MockUser> = {
  rep: {
    id: 'rep-001',
    name: 'Sarah Chen',
    email: 'sarah.chen@revops.app',
    role: 'rep',
    team: 'Enterprise Sales',
    title: 'Account Executive',
  },
  manager: {
    id: 'mgr-001',
    name: 'Michael Torres',
    email: 'michael.torres@revops.app',
    role: 'manager',
    team: 'Enterprise Sales',
    title: 'Sales Manager',
  },
  executive: {
    id: 'exec-001',
    name: 'Jennifer Walsh',
    email: 'jennifer.walsh@revops.app',
    role: 'executive',
    team: 'Executive',
    title: 'Chief Revenue Officer',
  },
};

interface RoleContextType {
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  currentUser: MockUser;
  availableRoles: { value: UserRole; label: string; description: string }[];
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const availableRoles: { value: UserRole; label: string; description: string }[] = [
  { 
    value: 'rep', 
    label: 'Sales Rep', 
    description: 'SDR/AE operational view' 
  },
  { 
    value: 'manager', 
    label: 'Manager', 
    description: 'Tactical team view' 
  },
  { 
    value: 'executive', 
    label: 'Executive', 
    description: 'C-Level strategic view' 
  },
];

export function RoleProvider({ children }: { children: ReactNode }) {
  const [currentRole] = useState<UserRole>('executive');
  const setCurrentRole = (_role: UserRole) => {}; // locked to executive

  const currentUser = mockUsers[currentRole];

  return (
    <RoleContext.Provider 
      value={{ 
        currentRole, 
        setCurrentRole, 
        currentUser, 
        availableRoles 
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}
